<!-- components/Survey/Question.svelte -->
<script>
  import { createEventDispatcher } from 'svelte';
  import { surveyStore } from '../../stores/surveyStore';
  import MultiChoice from './questions/MultiChoice.svelte';
  import ImageMultiChoice from './questions/ImageMultiChoice.svelte'; // New import
  import MultiSelect from './questions/MultiSelect.svelte';
  import Ranking from './questions/Ranking.svelte';
  import TeamSelection from './questions/TeamSelection.svelte';
  import InfoScreen from './questions/InfoSlide.svelte';

  export let question;
  export let isLast = false;
  
  const dispatch = createEventDispatcher();
  
  // Get the current value for this question from the store
  $: value = $surveyStore[question.id] || {};
  
  // Format value for component based on type
  $: formattedValue = formatValueForComponent(question.type, value);
  
  // Format value for specific component types
  function formatValueForComponent(type, value) {
    if (type === 'multi-select' || type === 'team-selection') {
      // These components expect arrays, but store might have object form
      if (Array.isArray(value)) {
        return value;
      } else if (typeof value === 'object') {
        // Convert object format (for checkboxes) to array format
        return Object.entries(value)
          .filter(([_, isSelected]) => isSelected)
          .map(([id, _]) => id);
      }
      return []; // Default to empty array
    } else if (type === 'ranking') {
      // Ranking expects an object mapping id to rank
      if (typeof value !== 'object' || Array.isArray(value)) {
        return {};
      }
      return value;
    }
    
    // Default case, return value as is
    return value;
  }
  
  function handleContinue() {
    // Dispatch event based on whether this is the last question
    console.log("Continue clicked, dispatching event");
    if (isLast) {
      dispatch('submit');
    } else {
      dispatch('answer');
    }
  }
  
  // For ranking questions
  function handleRankingUpdate(event) {
    console.log("Ranking update received:", event.detail);
    
    // Update the store with the new ranking
    surveyStore.update(store => ({
      ...store,
      [question.id]: event.detail
    }));
    
    // Automatically proceed to next question
    console.log("Auto-proceeding to next question after ranking");
    handleContinue();
  }
  
  // For multi-select questions
  function handleMultiSelectUpdate(event) {
    console.log("MultiSelect update received:", event.detail);
    
    // Update the store with the selected items - direct assignment, not merging
    const newValue = event.detail;
    
    surveyStore.update(store => {
      // Create a completely new store object to ensure reactivity
      const updatedStore = {
        ...store,
        [question.id]: newValue
      };
      
      console.log(`Updated store for ${question.id}:`, updatedStore[question.id]);
      return updatedStore;
    });
    
    // Don't automatically proceed - let the user click continue in the MultiSelect
  }
  
  // Initialize component state if needed
  $: {
    initializeComponentState(question);
  }
  
  function initializeComponentState(question) {
    if (!question) return;
    
    const currValue = $surveyStore[question.id];
    
    // Initialize based on component type
    if (question.type === 'ranking' && (!currValue || typeof currValue !== 'object' || Array.isArray(currValue))) {
      // Initialize ranking with default ranks (1, 2, 3, ...)
      const rankingValue = {};
      if (question.options) {
        question.options.forEach((option, index) => {
          rankingValue[option.id] = index + 1;
        });
      }
      
      // Update the store
      surveyStore.update(store => ({
        ...store,
        [question.id]: rankingValue
      }));
    } else if ((question.type === 'multi-select' || question.type === 'team-selection') && 
               (!currValue || (typeof currValue === 'object' && !Array.isArray(currValue) && Object.keys(currValue).length === 0))) {
      // Initialize multi-select and team-selection with empty array
      surveyStore.update(store => ({
        ...store,
        [question.id]: []
      }));
    }
  }
  
  // Check if the current question type is answered and valid
  $: isValid = () => {
    switch (question.type) {
      case 'multi-choice':
      case 'image-multi-choice': // Add validation for new type
        return $surveyStore[question.id] !== undefined;
      case 'multi-select':
        const multiSelectValue = $surveyStore[question.id];
        if (Array.isArray(multiSelectValue)) {
          return multiSelectValue.length > 0;
        }
        return multiSelectValue && Object.values(multiSelectValue).some(val => val === true);
      case 'ranking':
        return $surveyStore[question.id] && 
               Object.keys($surveyStore[question.id]).length === question.options.length;
      case 'team-selection':
        const selected = $surveyStore[question.id] || {};
        const selectedCount = Array.isArray(selected) ? 
          selected.length : 
          Object.values(selected).filter(v => v === true).length;
        return selectedCount >= (question.minSelect || 1);
      case 'info':
        return true; // Info screens are always valid
      default:
        return false;
    }
  };
  
  // Function to check if options have images
  function hasOptionImages(options) {
    return options && options.some(option => option.image);
  }
</script>

<div class="question">
  {#if question.type !== 'info'}
    <h2>{question.title || ''}</h2>
    
    {#if question.description}
      <p class="description">{question.description}</p>
    {/if}
  {/if}
  
  <div class="question-content">
    {#if question.type === 'multi-choice'}
      {#if hasOptionImages(question.options)}
        <!-- Use ImageMultiChoice if options have images -->
        <ImageMultiChoice 
          options={question.options} 
          questionId={question.id}
          title={question.title || ''}
        />
      {:else}
        <!-- Use standard MultiChoice if no images -->
        <MultiChoice 
          options={question.options} 
          questionId={question.id}
          title={question.title || ''}
        />
      {/if}
    {:else if question.type === 'multi-select'}
      <MultiSelect 
        options={question.options}
        title={question.title || ''}
        description={question.description || ''}
        currentValue={formattedValue}
        minSelect={question.minSelect || 0}
        on:select={handleMultiSelectUpdate}
        on:advance={handleContinue}
      />
    {:else if question.type === 'ranking'}
      <Ranking 
        title={question.title || ''}
        description={question.description || ''}
        options={question.options} 
        currentValue={$surveyStore[question.id] || {}}
        on:rank={handleRankingUpdate}
      />
    {:else if question.type === 'team-selection'}
      <TeamSelection 
        title={question.title || ''}
        description={question.description || ''}
        options={question.options}
        minSelect={question.minSelect || 3}
        currentValue={formattedValue}
        on:select={handleMultiSelectUpdate}
        on:advance={handleContinue}
      />
    {:else if question.type === 'info'}
      <InfoScreen
        title={question.title || ''}
        description={question.description || ''}
        icon={question.icon || null}
      />
    {/if}
  </div>
  
  {#if question.type !== 'ranking' && question.type !== 'multi-select' && question.type !== 'team-selection'}
    <!-- These components have their own continue buttons -->
    <div class="button-container">
      <button 
        class="continue-button" 
        on:click={handleContinue} 
        disabled={!isValid()}
      >
        {isLast ? 'Submit' : 'Continue'}
      </button>
    </div>
  {/if}
</div>

<style>
  .question {
    width: 100%;
  }
  
  h2 {
    font-size: 1.8rem;
    color: #333;
    margin-bottom: 1rem;
    font-weight: 600;
  }
  
  .description {
    font-size: 1.1rem;
    color: #666;
    margin-bottom: 2rem;
  }
  
  .question-content {
    margin-bottom: 2rem;
  }
  
  .button-container {
    display: flex;
    justify-content: center;
    margin-top: 2rem;
  }
  
  .continue-button {
    background-color: #6A0032; /* Cavs wine color */
    color: white;
    border: none;
    padding: 0.75rem 2rem;
    font-size: 1.1rem;
    border-radius: 30px;
    cursor: pointer;
    transition: background-color 0.2s ease;
    font-weight: 500;
  }
  
  .continue-button:hover:not([disabled]) {
    background-color: #6d0030;
  }
  
  .continue-button[disabled] {
    background-color: #cccccc;
    cursor: not-allowed;
  }
  @media (max-width: 768px) {
    h2 {
      font-size: 1.5rem;
      margin-bottom: 0.75rem;
    }
    
    .description {
      font-size: 1rem;
      margin-bottom: 1.5rem;
    }
    
    .question-content {
      margin-bottom: 1.5rem;
    }
  }
</style>